// src/review/canvas/sections/SecurityControlsSection.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Stack, Text, Divider, Badge, Group } from '@mantine/core';
import { useDispatch } from 'react-redux';
import EditableField from '../shared/EditableField';
import { useReview } from '../../context/ReviewContext';
import { saveSecurityControl } from '../../../redux/slices/securitySlice';

/**
 * SecurityControlsSection - Shows mitigation actions for security threats
 * Pulls data from threat.controls (enriched in SecurityReviewStep)
 * Matches SecurityTabPanel data structure
 */
function SecurityControlsSection({ threat }) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();

  // Get controls from enriched threat object
  const controls = threat.controls || [];

  // Handler for saving control name
  const handleSaveControlName = useCallback(
    (control) => async (newValue, oldValue) => {
      const controlData = {
        controlId: control.controlId,
        name: newValue,
        description: control.description || '',
      };

      await dispatch(
        saveSecurityControl({
          controlData,
          modelId,
          scenarioId: threat.scenarioId || threat.threatId,
          assessmentUuid
        })
      );
    },
    [dispatch, modelId, assessmentUuid, threat.scenarioId, threat.threatId]
  );

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="md" fw={700}>Mitigation Actions</Text>
          <Badge size="md" variant="light">
            {controls.length} {controls.length === 1 ? 'Action' : 'Actions'}
          </Badge>
        </Group>

        {controls.length > 0 ? (
          <Stack gap="sm">
            {controls.map((control, index) => (
              <div key={control.controlId || index}>
                {index > 0 && <Divider />}

                <EditableField
                  label={`Action ${index + 1}`}
                  value={control.name || 'No description'}
                  itemType="security_control"
                  itemId={control.controlId || `${threat.scenarioId}-control-${index}`}
                  fieldName="name"
                  onSave={handleSaveControlName(control)}
                  multiline
                  minRows={2}
                  placeholder="Describe the mitigation action..."
                />
              </div>
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">No mitigation actions defined for this threat.</Text>
        )}
      </Stack>
    </Card>
  );
}

SecurityControlsSection.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    scenarioId: PropTypes.string,
    controls: PropTypes.arrayOf(
      PropTypes.shape({
        controlId: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default SecurityControlsSection;
